import React, { useEffect, useState } from 'react';
import { Btn, C, Input } from '../../../components/UI';
import {
    platformApi, TenantTemplateDto, CreateTenantTemplateRequest,
    TemplatePricingRuleSeed, TemplateServiceCatalogItemSeed, MarketplaceAppAdminDto,
} from '../../../services/api';

const fieldLabel: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 500, color: C.textSub, marginBottom: 5 };
const textareaStyle: React.CSSProperties = { width: '100%', padding: '10px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: "'Inter', sans-serif", resize: 'vertical' };

// Same 19 keys TrySetFlag (TenantTemplateService.cs) and RequireFeatureAttribute recognize —
// this list is hand-maintained same as PlatformDashboard's own FLAG_LABELS.
const FLAG_KEYS: { key: string; label: string }[] = [
    { key: 'dumpsterRental', label: 'Dumpster Rental' },
    { key: 'loadingJobs', label: 'Loading Jobs' },
    { key: 'aiEstimation', label: 'AI Estimation' },
    { key: 'priceAdjustments', label: 'Price Adjustments' },
    { key: 'tipping', label: 'Tipping' },
    { key: 'ratings', label: 'Ratings' },
    { key: 'inAppChat', label: 'In-App Chat' },
    { key: 'backgroundCheckRequired', label: 'Background Check Required' },
    { key: 'serviceCatalogPricing', label: 'Service Catalog Pricing' },
    { key: 'estimatesAndQuotes', label: 'Estimates & Quotes' },
    { key: 'partialPayments', label: 'Partial Payments' },
    { key: 'contractorCertTracking', label: 'Contractor Cert Tracking' },
    { key: 'digitalWorkOrders', label: 'Digital Work Orders' },
    { key: 'inventoryTracking', label: 'Inventory Tracking' },
    { key: 'aiWarrantyLookup', label: 'AI Warranty Lookup' },
    { key: 'serviceFee', label: 'Service Fee' },
    { key: 'salesTax', label: 'Sales Tax' },
    { key: 'serviceAreaMapAndLicenses', label: 'Service Area Map + Business Licenses' },
    { key: 'estimateBuilder', label: 'Estimate Builder' },
    { key: 'propertyRecords', label: 'Property Records' },
    { key: 'complianceDocTracking', label: 'Compliance Document Tracking' },
    { key: 'photoChecklists', label: 'Photo Checklists' },
    { key: 'jobSlaTracking', label: 'Job SLA Tracking' },
    { key: 'clientPortal', label: 'Client Portal' },
    { key: 'donationCenters', label: 'Donation Centers' },
    { key: 'documentShredding', label: 'Document Shredding' },
];

type FlagState = 'default' | 'on' | 'off';

function pricingRulesToText(rows: TemplatePricingRuleSeed[]): string {
    return rows.map(r => `${r.serviceTypeName} | ${r.basePrice} | ${r.pricePerUnit} | ${r.unitLabel}`).join('\n');
}
function textToPricingRules(text: string): TemplatePricingRuleSeed[] {
    return text.split('\n').map(l => l.trim()).filter(Boolean).map(line => {
        const [name, base, perUnit, unit] = line.split('|').map(s => s.trim());
        return { serviceTypeName: name, basePrice: parseFloat(base) || 0, pricePerUnit: parseFloat(perUnit) || 0, unitLabel: unit || 'item' };
    });
}
function catalogItemsToText(rows: TemplateServiceCatalogItemSeed[]): string {
    return rows.map(r => `${r.name} | ${r.category} | ${r.pricingType} | ${r.price} | ${r.unit ?? ''}`).join('\n');
}
function textToCatalogItems(text: string): TemplateServiceCatalogItemSeed[] {
    return text.split('\n').map(l => l.trim()).filter(Boolean).map(line => {
        const [name, category, pricingType, price, unit] = line.split('|').map(s => s.trim());
        return { name, category: category || 'Other', pricingType: pricingType || 'Flat', price: parseFloat(price) || 0, unit: unit || null };
    });
}

export default function TenantTemplateEditForm({ template, onSaved, onCancel }: {
    template: TenantTemplateDto | null;
    onSaved: () => void;
    onCancel: () => void;
}) {
    const isNew = template === null;
    const [key, setKey] = useState(template?.key ?? '');
    const [name, setName] = useState(template?.name ?? '');
    const [description, setDescription] = useState(template?.description ?? '');
    const [flagStates, setFlagStates] = useState<Record<string, FlagState>>(() => {
        const initial: Record<string, FlagState> = {};
        for (const { key: k } of FLAG_KEYS) {
            initial[k] = template?.featureFlagOverrides?.[k] === true ? 'on'
                : template?.featureFlagOverrides?.[k] === false ? 'off' : 'default';
        }
        return initial;
    });
    const [selectedAppKeys, setSelectedAppKeys] = useState<Set<string>>(new Set(template?.marketplaceAppKeys ?? []));
    const [pricingRulesText, setPricingRulesText] = useState(pricingRulesToText(template?.starterPricingRules ?? []));
    const [catalogItemsText, setCatalogItemsText] = useState(catalogItemsToText(template?.starterServiceCatalogItems ?? []));
    const [serviceNoun, setServiceNoun] = useState(template?.serviceNoun ?? '');
    const [contractorNoun, setContractorNoun] = useState(template?.contractorNoun ?? '');

    const [apps, setApps] = useState<MarketplaceAppAdminDto[]>([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => { platformApi.getMarketplaceApps().then(setApps).catch(() => {}); }, []);

    const cycleFlag = (k: string) => {
        setFlagStates(prev => ({
            ...prev,
            [k]: prev[k] === 'default' ? 'on' : prev[k] === 'on' ? 'off' : 'default',
        }));
    };

    const toggleApp = (appKey: string) => {
        setSelectedAppKeys(prev => {
            const next = new Set(prev);
            if (next.has(appKey)) next.delete(appKey); else next.add(appKey);
            return next;
        });
    };

    const save = async () => {
        setSaving(true);
        setError('');

        const featureFlagOverrides: Record<string, boolean> = {};
        for (const [k, state] of Object.entries(flagStates)) {
            if (state === 'on') featureFlagOverrides[k] = true;
            else if (state === 'off') featureFlagOverrides[k] = false;
        }

        const payload: CreateTenantTemplateRequest = {
            key: key.trim(),
            name: name.trim(),
            description: description.trim(),
            featureFlagOverrides,
            marketplaceAppKeys: Array.from(selectedAppKeys),
            starterPricingRules: textToPricingRules(pricingRulesText),
            starterServiceCatalogItems: textToCatalogItems(catalogItemsText),
            serviceNoun: serviceNoun.trim() || null,
            contractorNoun: contractorNoun.trim() || null,
        };

        try {
            if (isNew) await platformApi.createTenantTemplate(payload);
            else await platformApi.updateTenantTemplate(template!.id, payload);
            onSaved();
        } catch (e: any) {
            setError(e.message || 'Failed to save.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            <button onClick={onCancel} style={{ background: 'none', border: 'none', color: C.textSub, fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: 16 }}>
                ← Tenant Templates
            </button>
            <h1 style={{ margin: '0 0 20px', fontSize: 20, fontWeight: 700, color: C.text }}>{isNew ? 'New template' : `Edit ${template!.name}`}</h1>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 4 }}>
                <Input label={'Key (stable slug, e.g. "landscaping")'} value={key} onChange={setKey} placeholder="my-vertical" required />
                <Input label="Name" value={name} onChange={setName} required />
            </div>
            <Input label="Description (shown to Platform Admin when onboarding a tenant)" value={description} onChange={setDescription} required />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <Input label={'Service noun override (e.g. "Visit" — optional)'} value={serviceNoun} onChange={setServiceNoun} />
                <Input label={'Contractor noun override (e.g. "Technician" — optional)'} value={contractorNoun} onChange={setContractorNoun} />
            </div>

            <div style={{ marginBottom: 18 }}>
                <label style={fieldLabel}>Feature flags — click to cycle Default → On → Off</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 6, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10 }}>
                    {FLAG_KEYS.map(({ key: k, label }) => {
                        const state = flagStates[k];
                        const [bg, color] = state === 'on' ? [C.greenBg, C.green] : state === 'off' ? ['#FFF0F0', '#9B1C1C'] : ['#F5F5F5', C.textMuted];
                        return (
                            <div key={k} onClick={() => cycleFlag(k)} style={{
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer',
                                padding: '6px 10px', borderRadius: 6, background: bg,
                            }}>
                                <span style={{ fontSize: 12.5, color: C.text }}>{label}</span>
                                <span style={{ fontSize: 10, fontWeight: 700, color, textTransform: 'uppercase' }}>{state}</span>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div style={{ marginBottom: 18 }}>
                <label style={fieldLabel}>Marketplace apps to install ({selectedAppKeys.size} selected) — Paid apps are suggested to the tenant, never auto-installed</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 6, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, maxHeight: 220, overflowY: 'auto' }}>
                    {apps.length === 0 && <div style={{ color: C.textMuted, fontSize: 12.5 }}>No apps in the catalog yet.</div>}
                    {apps.map(a => (
                        <label key={a.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: C.text, cursor: 'pointer' }}>
                            <input type="checkbox" checked={selectedAppKeys.has(a.key)} onChange={() => toggleApp(a.key)} />
                            {a.name} <span style={{ color: C.textMuted }}>({a.category}{a.pricingModel === 'Paid' ? ', Paid' : ''})</span>
                        </label>
                    ))}
                </div>
            </div>

            <div style={{ marginBottom: 14 }}>
                <label style={fieldLabel}>Starter pricing rules — one per line: "Service name | Base price | Price per unit | Unit label"</label>
                <textarea value={pricingRulesText} onChange={e => setPricingRulesText(e.target.value)} rows={3} style={textareaStyle} placeholder="Lawn Mowing | 45 | 0 | visit" />
            </div>

            <div style={{ marginBottom: 14 }}>
                <label style={fieldLabel}>Starter service catalog items — one per line: "Name | Category | PricingType (Flat/Hourly/PartsMarkup) | Price | Unit"</label>
                <textarea value={catalogItemsText} onChange={e => setCatalogItemsText(e.target.value)} rows={3} style={textareaStyle} placeholder="Property Inspection | Inspections | Flat | 65 | visit" />
            </div>

            {error && <div style={{ color: '#D84040', fontSize: 13, marginBottom: 14 }}>{error}</div>}

            <div style={{ display: 'flex', gap: 10 }}>
                <Btn variant="outline" onClick={onCancel}>Cancel</Btn>
                <Btn onClick={save} disabled={saving || !key.trim() || !name.trim() || !description.trim()}>
                    {saving ? 'Saving…' : isNew ? 'Create template' : 'Save changes'}
                </Btn>
            </div>
        </div>
    );
}
